import { useState, useEffect, FormEvent } from 'react';
import { getAuthUser, apiRequest, BackendPost } from '../lib/api';

type EditablePageHeaderProps = {
  pageKey: string;
  defaultEyebrow: string;
  defaultTitle: string;
  children?: React.ReactNode;
};

export default function EditablePageHeader({ pageKey, defaultEyebrow, defaultTitle, children }: EditablePageHeaderProps) {
  const user = getAuthUser();
  const [isEditing, setIsEditing] = useState(false);
  const [eyebrow, setEyebrow] = useState(defaultEyebrow);
  const [title, setTitle] = useState(defaultTitle);
  const [editEyebrow, setEditEyebrow] = useState(defaultEyebrow);
  const [editTitle, setEditTitle] = useState(defaultTitle);
  const [post, setPost] = useState<BackendPost | null>(null);
  const [allMeta, setAllMeta] = useState<Record<string, { eyebrow: string; title: string }>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEditing) {
      const prevBodyOverflow = document.body.style.overflow;
      const prevHtmlOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevBodyOverflow;
        document.documentElement.style.overflow = prevHtmlOverflow;
      };
    }
  }, [isEditing]);

  useEffect(() => {
    async function loadMeta() {
      try {
        const res = await apiRequest<{ posts: BackendPost[] }>('/posts');
        const metaPost = res.posts.find((p) => p.slug === 'site-meta');
        if (metaPost) {
          setPost(metaPost);
          const parsed = JSON.parse(metaPost.content);
          setAllMeta(parsed);
          if (parsed[pageKey]) {
            setEyebrow(parsed[pageKey].eyebrow || defaultEyebrow);
            setTitle(parsed[pageKey].title || defaultTitle);
          }
        }
      } catch (e) {
        // Ignore errors if meta doesn't exist yet
      }
    }
    loadMeta();
  }, [pageKey, defaultEyebrow, defaultTitle]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const newMeta = { ...allMeta, [pageKey]: { eyebrow: editEyebrow, title: editTitle } };
      const payload = {
        title: 'Site Meta',
        slug: 'site-meta',
        excerpt: 'Global site metadata',
        content: JSON.stringify(newMeta),
        category: 'System',
      };

      if (post) {
        const res = await apiRequest<{ post: BackendPost }>(`/posts/${post.id}`, { method: 'PUT', body: JSON.stringify(payload) }, true);
        setPost(res.post);
        setAllMeta(newMeta);
      } else {
        const res = await apiRequest<{ post: BackendPost }>('/posts', { method: 'POST', body: JSON.stringify(payload) }, true);
        setPost(res.post);
        setAllMeta(newMeta);
      }
      setEyebrow(editEyebrow);
      setTitle(editTitle);
      setIsEditing(false);
    } catch (err) {
      alert('Không thể lưu thay đổi tiêu đề');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="section-heading" style={{ position: 'relative' }}>
      <span className="eyebrow">{eyebrow}</span>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
        <h1 style={{ 
          margin: 0, 
          flex: 1,
          minWidth: 0,
          marginRight: '12px',
          wordBreak: 'break-word',
        }}>
          {title}
        </h1>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
          {children}
          {user?.role === 'ADMIN' && (
            <button
              className="admin-action-btn"
              style={{ flexShrink: 0 }}
              onClick={() => {
                setEditEyebrow(eyebrow);
                setEditTitle(title);
                setIsEditing(true);
              }}
            >
              <span className="icon">✎</span>
              Chỉnh sửa
            </button>
          )}
        </div>
      </div>

      {isEditing && (
        <div 
          className="modal-overlay" 
          onClick={() => setIsEditing(false)} 
          onWheel={(e) => e.stopPropagation()}
          style={{ zIndex: 9999, overscrollBehavior: 'contain' }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', overscrollBehavior: 'contain' }}>
            <button className="modal-close" onClick={() => setIsEditing(false)}>×</button>
            <h2 style={{ borderBottom: '1px solid var(--glass-border)', paddingBottom: '16px', color: '#f8fafc', margin: '0 0 20px 0' }}>Sửa Tiêu Đề Trang</h2>
            <form onSubmit={handleSave} className="admin-form">
              <label className="field-label">Thẻ (Eyebrow)</label>
              <input 
                className="input-box" 
                value={editEyebrow} 
                onChange={(e) => setEditEyebrow(e.target.value)} 
                required 
              />
              
              <label className="field-label" style={{ marginTop: '16px' }}>Tiêu đề chính (H1)</label>
              <input 
                className="input-box" 
                value={editTitle} 
                onChange={(e) => setEditTitle(e.target.value)} 
                required 
              />

              <div className="modal-actions" style={{ marginTop: '28px', borderTop: '1px solid var(--glass-border)', paddingTop: '20px' }}>
                <button type="button" className="button outline" onClick={() => setIsEditing(false)}>Hủy</button>
                <button type="submit" className="button primary" disabled={saving}>
                  {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
